import { isServiceLocked } from '@/lib/serviceLock'

/**
 * A period during which a service was closed.
 *
 * `status` alone is a single bit and cannot express "invoiced, reopened,
 * invoiced again" — so anything asking *was this record closed when X
 * happened* has to be answered from the status-change history instead. An
 * epoch is one lock and the reopen that ended it; `reopenedAt` is absent while
 * the record is still closed.
 */
export interface LockEpoch {
  /** The terminal status that closed the record (Invoiced or Cancelled). */
  status: string
  /** Id of the status change that closed it, so the feed can key off it. */
  statusChangeId: string
  lockedAt: string
  lockedById: string
  reopenedAt?: string
}

interface StatusChangeLike {
  id: string
  authorId: string
  createdAt: string
  newStatus: string
}

/**
 * Every locked period in a service's history, oldest first.
 *
 * Timestamps come from `createdAt` — when the transition was *recorded* — to
 * match how notes are stamped. `occurredAt` describes the real-world event and
 * can predate the record of it, which would make intervals overlap.
 */
export function deriveLockEpochs(
  statusChanges: StatusChangeLike[] | undefined,
): LockEpoch[] {
  const ordered = [...(statusChanges ?? [])].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )

  const epochs: LockEpoch[] = []

  for (const change of ordered) {
    const current = epochs[epochs.length - 1]
    const isOpen = current !== undefined && current.reopenedAt === undefined

    if (isServiceLocked(change.newStatus)) {
      // Moving between two terminal statuses never reopens anything, so it
      // extends the period already running rather than starting a new one.
      if (!isOpen) {
        epochs.push({
          status: change.newStatus,
          statusChangeId: change.id,
          lockedAt: change.createdAt,
          lockedById: change.authorId,
        })
      }
      continue
    }

    if (isOpen) current.reopenedAt = change.createdAt
  }

  return epochs
}

/** Whether the service was closed at `at`. */
export function wasLockedAt(
  epochs: LockEpoch[],
  at: string | undefined,
): boolean {
  if (!at) return false
  return epochs.some(
    (epoch) =>
      epoch.lockedAt <= at &&
      (epoch.reopenedAt === undefined || at < epoch.reopenedAt),
  )
}

/** When this note was last rewritten, or undefined if it never was. */
export function lastRevisedAt(
  revisions: Array<{ at: string }> | undefined,
): string | undefined {
  if (!revisions || revisions.length === 0) return undefined
  return revisions.reduce(
    (latest, revision) => (revision.at > latest ? revision.at : latest),
    revisions[0].at,
  )
}
